/* File: src/components/game/GameResults.js */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import FocusMeter from '../dashboard/FocusMeter';
import FeatureIntroMessage from './FeatureIntroMessage';
import CoachingMessage from './CoachingMessage';
import { getTierName, formatCombinedDifficulty } from '../../contexts/gameHelpers';
import { formatDuration } from '../dashboard/utils/formatters';
import { useProfile } from '../../contexts/ProfileContext';
import CommentModal from '../ui/CommentModal';
import ConfirmationModal from '../ui/ConfirmationModal'; // Import ConfirmationModal
import { sanitizeTag } from '../../contexts/profile/profileUtils';
import { useGame } from '../../contexts/GameContext';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBook, faPlay, faRotateRight, faVideo, faHouse,
    faChartLine, faLightbulb, faChartBar, faArrowLeft, faArrowRight,
} from '@fortawesome/free-solid-svg-icons';

const GameResults = ({
  gameResults,
  feedbackMessage,
  getFeatureIntroMessage,
  onDashboard,
  onNavigate, // Function to navigate to different routes/views
  resetResponses // Function to clear response arrays before next game
}) => {
    // --- Context & State ---
    const { activeProfile, addJournalEntry, addJournalTag } = useProfile();
    // --- MODIFICATION: Use GameContext actions ---
    const { startGame, goToPreviousNBackLevel, goToNextNBackLevelZero } = useGame();

    const [isSavingToJournal, setIsSavingToJournal] = useState(false);
    const [journalSaveStatus, setJournalSaveStatus] = useState(null);
    const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [isConfirmBackModalOpen, setIsConfirmBackModalOpen] = useState(false); // State for confirmation modal

    // --- Data Extraction ---
    useEffect(() => {
        // console.log("GameResults received:", gameResults);
        // console.log("Active Profile in GameResults:", activeProfile);
    }, [gameResults, activeProfile]);

    const {
        // Core data for button logic:
        nBack: nBackPlayed,             // N-level of the session just completed
        difficulty: difficultyPlayed,   // Difficulty (float) of the session just completed
        newNBack: dAlgoNBack,           // Suggested N-Back from d'algo
        newDifficulty: dAlgoDifficulty, // Suggested difficulty (float) from d'algo

        // Other existing properties:
        replayData = null, sessionId = null, isPlayground = false, isChallengeAttempt = false,
        isPartialResult = false, trialsCompleted = 0, totalTrialsPlanned = 0,
        // oldNBack and oldDifficulty are effectively nBackPlayed and difficultyPlayed
        overallDprime = null, overallAccuracy = null, visualAccuracy = null,
        visualCorrect = null, visualTargetCount = null, visualFalseAlarms = null,
        visualDprime = null, audioAccuracy = null, audioCorrect = null,
        audioTargetCount = null, audioFalseAlarms = null, audioDprime = null,
        excellentThreshold = null, goodThreshold = null, fairThreshold = null,
        showReport = true, // showRetryOption might be deprecated by new button logic
        durationMs = null, username = 'Player',
        // challengeSuccess might be true if a challenge was just passed
        challengeSuccess = false,
        // sessionNBack is also available from backend, equivalent to nBackPlayed here
    } = gameResults || {};


    // --- Derived data & Formatting ---
    const hasValidReplayDataStructure = !!replayData?.settings && Array.isArray(replayData.events) && replayData.startTime !== undefined;
    const canReplay = (!!sessionId || (isChallengeAttempt && hasValidReplayDataStructure)) && hasValidReplayDataStructure;
    const canJournal = !isPlayground && !!sessionId && !isChallengeAttempt;

    const introMessage = typeof getFeatureIntroMessage === 'function' ? getFeatureIntroMessage(dAlgoDifficulty) : null; // Use d'algo difficulty for intro
    const playedCombinedDisplay = formatCombinedDifficulty(nBackPlayed, difficultyPlayed); // Level played
    const dAlgoSuggestedDisplay = formatCombinedDifficulty(dAlgoNBack, dAlgoDifficulty); // d' algo suggested next

    const formatNum = (num, decimals = 2) => ((typeof num === 'number' && Number.isFinite(num)) ? num.toFixed(decimals) : 'N/A');
    const formatAcc = (num) => formatNum(num, 1);

    const isGoodOrBetterPerformance = useMemo(() => {
        if (overallDprime === null || goodThreshold === null) return false;
        return overallDprime >= goodThreshold;
    }, [overallDprime, goodThreshold]);

    const isFailedChallengeAttempt = isChallengeAttempt === true && !isGoodOrBetterPerformance && !challengeSuccess;


    // --- Performance Assessment (Using Dynamic Thresholds) ---
    let veryGoodT = null;
    const thresholdsValid = excellentThreshold !== null && goodThreshold !== null && fairThreshold !== null;
    if (thresholdsValid) {
        veryGoodT = goodThreshold + (excellentThreshold - goodThreshold) * 0.6;
    }
    const getPerformanceCategory = (dprime, excellentT, goodT, fairT, calculatedVeryGoodT) => {
        const useDynamicThresholds = excellentT !== null && goodT !== null && fairT !== null && calculatedVeryGoodT !== null;
        if (dprime === null || typeof dprime !== 'number') return 'unknown';
        if (useDynamicThresholds) {
            if (dprime >= excellentT) return 'excellent';
            if (dprime >= calculatedVeryGoodT) return 'very-good';
            if (dprime >= goodT) return 'good';
            if (dprime >= fairT) return 'fair';
            return 'needs-improvement';
        } else {
            console.warn("GameResults: Dynamic thresholds missing or invalid. Using fallback interpretation for category.");
            if (dprime >= 2.0) return 'good';
            if (dprime >= 1.0) return 'fair';
            return 'needs-improvement';
        }
    };
    const performanceCategory = getPerformanceCategory(overallDprime, excellentThreshold, goodThreshold, fairThreshold, veryGoodT);
    const getPerformanceDisplay = (category) => {
        switch(category) {
            case 'excellent': return { text: 'Excellent', colorClass: 'text-green-600 bg-green-50 border border-green-200' };
            case 'very-good': return { text: 'Very Good', colorClass: 'text-blue-600 bg-blue-50 border border-blue-200' };
            case 'good': return { text: 'Good', colorClass: 'text-indigo-600 bg-indigo-50 border border-indigo-200' };
            case 'fair': return { text: 'Fair', colorClass: 'text-amber-600 bg-amber-50 border border-amber-200' };
            case 'needs-improvement': return { text: 'Needs Improvement', colorClass: 'text-red-600 bg-red-50 border border-red-200' };
            default: return { text: 'Unknown', colorClass: 'text-gray-600 bg-gray-50 border border-gray-200' };
        }
    };
    const performanceDisplay = getPerformanceDisplay(performanceCategory);


    // --- Button Handlers ---
    const handleRetryCurrentAttempt = useCallback(() => {
        if (resetResponses) resetResponses();
        startGame({ overrideNBack: nBackPlayed, overrideDifficulty: difficultyPlayed });
    }, [resetResponses, startGame, nBackPlayed, difficultyPlayed]);

    const handleContinueAlgorithmNext = useCallback(() => {
        if (resetResponses) resetResponses();
        startGame({ overrideNBack: dAlgoNBack, overrideDifficulty: dAlgoDifficulty });
    }, [resetResponses, startGame, dAlgoNBack, dAlgoDifficulty]);

    const openGoBackConfirmation = useCallback(() => {
        setIsConfirmBackModalOpen(true);
    }, []);

    const confirmGoBackAndStart = useCallback(() => {
        if (resetResponses) resetResponses();
        goToPreviousNBackLevel();
        setIsConfirmBackModalOpen(false);
    }, [resetResponses, goToPreviousNBackLevel]);

    const cancelGoBack = useCallback(() => {
        setIsConfirmBackModalOpen(false);
    }, []);

    const handleGoToNextMajorLevel = useCallback(() => {
        if (resetResponses) resetResponses();
        // Pass the nBack and difficulty of the level just played
        goToNextNBackLevelZero(nBackPlayed, difficultyPlayed);
    }, [resetResponses, goToNextNBackLevelZero, nBackPlayed, difficultyPlayed]);


    // --- Button Definitions ---
    const continueButtonConfig = useMemo(() => ({
        text: `Continue at ${dAlgoSuggestedDisplay}`,
        action: handleContinueAlgorithmNext,
        icon: faPlay,
        title: `Continue to your next suggested level ${dAlgoSuggestedDisplay}`,
        style: "primary" // This will be the main styled button
    }), [dAlgoSuggestedDisplay, handleContinueAlgorithmNext]);

    const retryButtonConfig = useMemo(() => ({
        text: `Retry ${playedCombinedDisplay}`,
        action: handleRetryCurrentAttempt,
        icon: faRotateRight,
        title: `Retry level ${playedCombinedDisplay}`,
        style: "secondary"
    }), [playedCombinedDisplay, handleRetryCurrentAttempt]);
    
    const getPrevNBackStoredLevelDisplay = useCallback(() => {
        if (!activeProfile || !nBackPlayed || nBackPlayed <= 1) return null;
        const prevN = nBackPlayed - 1;
        const prevDiffStored = activeProfile.previousNBackLevelDifficulties?.[prevN];
        const prevDiffHundredths = prevDiffStored !== undefined ? Math.round(prevDiffStored * 100) : 0;
        const prevDiffDisplayStr = prevDiffHundredths.toString().padStart(2, '0');
        return `${prevN}.${prevDiffDisplayStr}`;
    }, [activeProfile, nBackPlayed]);

    const prevNBackStoredLevelDisplay = getPrevNBackStoredLevelDisplay();

    const backToPreviousStoredButtonConfig = useMemo(() => {
        const difficultyPlayedHundredths = Math.round((difficultyPlayed || 0) * 100);
        const isLowDifficultyRange = difficultyPlayedHundredths >= 0 && difficultyPlayedHundredths <= 19;
        const canGoBack = (nBackPlayed || 0) > 2; // Only allow going back if current N > 2 (e.g., from 3-back to 2-back)

        if (isLowDifficultyRange && canGoBack && !!prevNBackStoredLevelDisplay) {
            return {
                text: `Back to ${prevNBackStoredLevelDisplay}`,
                action: openGoBackConfirmation,
                icon: faArrowLeft,
                title: `Go back to your previously stored level ${prevNBackStoredLevelDisplay}`,
                style: "secondary"
            };
        }
        return null;
    }, [difficultyPlayed, nBackPlayed, prevNBackStoredLevelDisplay, openGoBackConfirmation]);

    const nextMajorLevelButtonConfig = useMemo(() => {
        const difficultyPlayedHundredths = Math.round((difficultyPlayed || 0) * 100);
        const maxNBack = 20; // Assuming max n-back is 20
        // Show if difficulty played is N.50 or higher
        if (difficultyPlayedHundredths >= 50 && nBackPlayed < maxNBack) {
            const nextN = nBackPlayed + 1;
            return {
                text: `Next Level: ${nextN}.00`,
                action: handleGoToNextMajorLevel,
                icon: faArrowRight,
                title: `Advance to ${nextN}-Back at level ${nextN}.00`,
                style: "secondary"
            };
        }
        return null;
    }, [difficultyPlayed, nBackPlayed, handleGoToNextMajorLevel]);

    // Determine active buttons for layout
    // Order: Continue (primary), then (Next Level OR Back To), then Retry
    const actionButtons = [
        continueButtonConfig,
        nextMajorLevelButtonConfig || backToPreviousStoredButtonConfig, // Only one of these can be active at a time
        retryButtonConfig
    ].filter(Boolean); // Remove nulls if a conditional button isn't active


    // --- Event Handlers (Replay, Journal, Tabs) ---
    const handleReplay = useCallback(() => {
        if (!canReplay || typeof onNavigate !== 'function' || !sessionId) return;
        onNavigate(`/replay/${sessionId}`);
    }, [onNavigate, canReplay, sessionId]);

    const handleOpenJournalModal = useCallback(() => {
        if (isSavingToJournal || journalSaveStatus === 'success' || !canJournal) return;
        setIsCommentModalOpen(true);
    }, [isSavingToJournal, journalSaveStatus, canJournal]);

    const handleCloseJournalModal = useCallback(() => setIsCommentModalOpen(false), []);

    const handleSaveJournalEntry = useCallback(async (comment, userTags = []) => {
        if (!activeProfile || !sessionId || isSavingToJournal || !canJournal) return;
        setIsSavingToJournal(true); setJournalSaveStatus('saving');
        const sanitizedUserTags = [...new Set((userTags || []).map(tag => sanitizeTag(tag)).filter(Boolean))].sort();
        const entryData = {
            type: 'session_summary',
            summaryData: {
                oldNBack: nBackPlayed ?? null, oldDifficulty: difficultyPlayed ?? null,
                newNBack: dAlgoNBack ?? null, newDifficulty: dAlgoDifficulty ?? null,
                durationMs: durationMs ?? null, trialsCompleted: trialsCompleted ?? 0,
                totalTrialsPlanned: totalTrialsPlanned ?? 0, overallDprime: overallDprime ?? null,
                overallAccuracy: overallAccuracy ?? null, visualAccuracy: visualAccuracy ?? null,
                visualCorrect: visualCorrect ?? null, visualTargetCount: visualTargetCount ?? null,
                visualFalseAlarms: visualFalseAlarms ?? null, visualDprime: visualDprime ?? null,
                audioAccuracy: audioAccuracy ?? null, audioCorrect: audioCorrect ?? null,
                audioTargetCount: audioTargetCount ?? null, audioFalseAlarms: audioFalseAlarms ?? null,
                audioDprime: audioDprime ?? null, excellentThreshold: excellentThreshold ?? null,
                goodThreshold: goodThreshold ?? null, fairThreshold: fairThreshold ?? null,
                replayNavData: canReplay ? replayData : null, sessionId: sessionId
            },
            sessionId: sessionId, timestamp: new Date(), comment: comment || "",
            tags: sanitizedUserTags, nBack: nBackPlayed ?? 'N/A', difficulty: difficultyPlayed ?? 'N/A',
            hasReplay: canReplay,
        };
        try {
            const successResult = await addJournalEntry(activeProfile.id, entryData);
            if (successResult && successResult.id) {
                if (sanitizedUserTags.length > 0) {
                    const newTagsToProfile = sanitizedUserTags.filter(tag => !(activeProfile.journalTags || []).includes(tag));
                    if (newTagsToProfile.length > 0) await addJournalTag(activeProfile.id, newTagsToProfile);
                }
                setJournalSaveStatus('success');
                setTimeout(() => { handleCloseJournalModal(); setTimeout(() => setJournalSaveStatus(null), 500); }, 1500);
            } else throw new Error("addJournalEntry failed or returned unexpected value.");
        } catch (error) {
            console.error("Failed to save journal entry:", error); setJournalSaveStatus('error');
        } finally {
            setIsSavingToJournal(false);
        }
    }, [
        activeProfile, sessionId, isSavingToJournal, canJournal, addJournalEntry, addJournalTag, handleCloseJournalModal,
        nBackPlayed, difficultyPlayed, dAlgoNBack, dAlgoDifficulty, durationMs, trialsCompleted, totalTrialsPlanned,
        overallDprime, overallAccuracy, visualAccuracy, visualCorrect, visualTargetCount, visualFalseAlarms, visualDprime,
        audioAccuracy, audioCorrect, audioTargetCount, audioFalseAlarms, audioDprime, canReplay, replayData,
        excellentThreshold, goodThreshold, fairThreshold
    ]);
    
    const handleTabChange = (tab) => setActiveTab(tab);

    // --- Render Logic ---
    if (showReport === false && !isChallengeAttempt && !isPlayground) {
        return null;
    }
    if (!gameResults) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 text-red-600">
                Error: Game results data is missing.
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
                {/* Header Section */}
                <div className="px-4 sm:px-6 py-5 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
                            {isPlayground ? 'Playground Results' : isFailedChallengeAttempt ? 'Challenge Attempt' : (challengeSuccess ? 'Challenge Complete!' : 'Session Complete')}
                        </h1>
                        <div className="mt-1.5 flex flex-wrap gap-2 items-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${performanceDisplay.colorClass}`}>
                                {performanceDisplay.text}
                            </span>
                             {overallDprime !== null && (
                                 <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-300">
                                    d′: {formatNum(overallDprime, 2)}
                                 </span>
                             )}
                             {durationMs !== null && (
                                 <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-300">
                                     {formatDuration(durationMs)}
                                 </span>
                             )}
                            {isPartialResult && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                                    Partial: {trialsCompleted}/{totalTrialsPlanned} trials
                                </span>
                            )}
                            {isPlayground && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-300">
                                    Playground Mode
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex-shrink-0 mt-2 sm:mt-0 flex items-center space-x-2">
                        {canReplay && (
                            <button onClick={handleReplay} className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" title="Replay this session">
                                <FontAwesomeIcon icon={faVideo} className="w-4 h-4 mr-1.5" /> Replay
                            </button>
                        )}
                        {canJournal && (
                            <button onClick={handleOpenJournalModal} disabled={isSavingToJournal || journalSaveStatus === 'success'} className={`inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${ journalSaveStatus === 'success' ? 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 cursor-default' : journalSaveStatus === 'error' ? 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500' : 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed' }`} title={journalSaveStatus === 'success' ? "Added!" : journalSaveStatus === 'error' ? "Save Failed" : "Add to Journal"}>
                                <FontAwesomeIcon icon={faBook} className="w-4 h-4 mr-1.5" /> {isSavingToJournal ? 'Saving...' : journalSaveStatus === 'success' ? 'Added' : journalSaveStatus === 'error' ? 'Save Failed' : 'Add to Journal'}
                            </button>
                        )}
                    </div>
                </div>

                {/* Tabs Navigation */}
                <div className="border-b border-gray-200">
                     <nav className="flex -mb-px px-4 sm:px-6" aria-label="Tabs">
                         <button onClick={() => handleTabChange('overview')} className={`py-3 px-4 text-sm font-medium border-b-2 flex items-center ${activeTab === 'overview' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} aria-current={activeTab === 'overview' ? 'page' : undefined}>
                            <FontAwesomeIcon icon={faChartLine} className={`w-4 h-4 mr-2 ${activeTab === 'overview' ? 'text-indigo-500' : 'text-gray-400'}`} /> Overview
                         </button>
                         <button onClick={() => handleTabChange('detailed')} className={`py-3 px-4 text-sm font-medium border-b-2 flex items-center ${activeTab === 'detailed' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} aria-current={activeTab === 'detailed' ? 'page' : undefined}>
                             <FontAwesomeIcon icon={faChartBar} className={`w-4 h-4 mr-2 ${activeTab === 'detailed' ? 'text-indigo-500' : 'text-gray-400'}`} /> Detailed Stats
                         </button>
                     </nav>
                </div>

                {/* Tab Content Area */}
                <div className="p-4 sm:p-6">
                    {activeTab === 'overview' && (
                        <div className="animate-[fadeIn_0.3s_ease-in]">
                            <div className="mb-6 p-4 rounded-lg border border-gray-200 bg-gray-50">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                                    <div className="text-center flex-1">
                                        <div className="text-sm text-gray-500 mb-1">Overall Sensitivity (d′)</div>
                                        <div className={`text-3xl font-bold ${performanceDisplay.colorClass.split(' ')[0]}`}>{formatNum(overallDprime, 2)}</div>
                                        <div className="text-xs text-gray-500 mt-1">{performanceDisplay.text}</div>
                                    </div>
                                    <div className="hidden sm:block h-14 w-px bg-gray-200"></div>
                                    <div className="flex-1 grid grid-cols-2 gap-2 text-center">
                                        <div>
                                            <div className="text-sm text-gray-500 mb-1">Visual</div> <div className="font-bold text-lg text-blue-600">{formatAcc(visualAccuracy)}%</div> <div className="text-xs text-gray-500">d′: {formatNum(visualDprime, 1)}</div>
                                        </div>
                                        <div>
                                            <div className="text-sm text-gray-500 mb-1">Audio</div> <div className="font-bold text-lg text-green-600">{formatAcc(audioAccuracy)}%</div> <div className="text-xs text-gray-500">d′: {formatNum(audioDprime, 1)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div> {/* Closing div for the Overall Sensitivity block */}
                             {!isPlayground && !isFailedChallengeAttempt && (
                                <div className="mb-6 p-4 rounded-lg border border-gray-200 bg-white">
                                    <h3 className="text-sm font-medium text-gray-700 mb-2">Level Progression</h3>
                                    <div className="flex justify-between items-center mb-3">
                                        <div className="flex items-center text-gray-700">
                                            <span className="text-lg font-semibold mr-3">{playedCombinedDisplay}</span>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400"> <path d="M5 12h14"></path> <path d="m12 5 7 7-7 7"></path> </svg>
                                            <span className={`text-lg font-semibold ml-3 ${ dAlgoNBack > nBackPlayed ? 'text-green-600' : dAlgoNBack < nBackPlayed ? 'text-red-600' : dAlgoDifficulty > difficultyPlayed ? 'text-green-600' : dAlgoDifficulty < difficultyPlayed ? 'text-red-600' : 'text-gray-700' }`}>
                                                {dAlgoSuggestedDisplay}
                                            </span>
                                        </div>
                                        <div className="text-xs text-gray-500"> Based on d′: {formatNum(overallDprime, 2)} </div>
                                    </div>
                                    {dAlgoNBack > nBackPlayed && ( <div className="bg-green-50 border-l-4 border-green-400 p-3 text-green-800 text-sm rounded-r-md"><div className="flex items-start"><div className="text-green-500 mr-2 text-lg" aria-hidden="true">🎉</div><div>Advanced to {dAlgoNBack}-back! Starting at {dAlgoSuggestedDisplay}.</div></div></div> )}
                                    {dAlgoNBack < nBackPlayed && ( <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 text-yellow-800 text-sm rounded-r-md"><div className="flex items-start"><div className="text-yellow-600 mr-2 text-lg" aria-hidden="true">⚠️</div><div>Adjusting level: Moving to {dAlgoNBack}-back ({dAlgoSuggestedDisplay}).</div></div></div> )}
                                    {dAlgoNBack === nBackPlayed && feedbackMessage && ( <div className="mt-3"> <CoachingMessage message={feedbackMessage} /> </div> )}
                                    {dAlgoNBack === nBackPlayed && !feedbackMessage && ( <div className="mt-3 bg-gray-50 border-l-4 border-gray-400 p-3 text-gray-700 text-sm rounded-r-md"> Level maintained at {dAlgoSuggestedDisplay}. Keep practicing! </div> )}
                                </div>
                             )}
                            {isFailedChallengeAttempt && ( <div className="mb-6"><div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-3 text-orange-800 rounded-r-md"><p className="font-medium">Challenge Attempt Unsuccessful</p><p className="mt-1">Your progress level ({playedCombinedDisplay}) remains unchanged. {goodThreshold !== null ? `Achieve d' ≥ ${formatNum(goodThreshold, 2)} on Level ${formatCombinedDifficulty(nBackPlayed, 0.99)} to advance.` : 'Try again to advance.'}</p></div></div> )}
                            {challengeSuccess && ( <div className="mb-6"><div className="bg-green-50 border-l-4 border-green-400 p-4 text-green-800 rounded-r-md"><p className="font-medium">Challenge Completed Successfully!</p><p className="mt-1">Congratulations! You've advanced to {dAlgoNBack}-back. Start your training at {dAlgoSuggestedDisplay}.</p></div></div> )}
                            {introMessage && ( <div className="mb-6"> <FeatureIntroMessage message={introMessage} /> </div> )}
                        </div>
                    )}

                    {activeTab === 'detailed' && (
                        <div className="animate-[fadeIn_0.3s_ease-in] max-h-[60vh] overflow-y-auto">
                            {/* Detailed stats content as before */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                    <div className="flex items-center mb-3"> <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg></div><h4 className="font-semibold text-gray-800">Visual Performance</h4></div>
                                    <div className="space-y-2 text-sm"> <div className="flex justify-between"><span className="text-gray-600">Accuracy:</span><span className="font-medium text-gray-800">{formatAcc(visualAccuracy)}%</span></div> <div className="flex justify-between"><span className="text-gray-600">Hits:</span><span className="text-gray-800">{visualCorrect ?? '?'} / {visualTargetCount ?? '?'}</span></div> <div className="flex justify-between"><span className="text-gray-600">False Alarms:</span><span className="text-gray-800">{visualFalseAlarms ?? '?'}</span></div> <div className="pt-2 border-t border-gray-100 mt-2"><div className="flex justify-between items-center"><span className="text-gray-600">d′ Score:</span><span className="font-medium text-blue-600 text-base">{formatNum(visualDprime, 2)}</span></div></div> </div>
                                </div>
                                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                    <div className="flex items-center mb-3"> <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-2"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-600" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" /></svg></div><h4 className="font-semibold text-gray-800">Audio Performance</h4></div>
                                    <div className="space-y-2 text-sm"> <div className="flex justify-between"><span className="text-gray-600">Accuracy:</span><span className="font-medium text-gray-800">{formatAcc(audioAccuracy)}%</span></div> <div className="flex justify-between"><span className="text-gray-600">Hits:</span><span className="text-gray-800">{audioCorrect ?? '?'} / {audioTargetCount ?? '?'}</span></div> <div className="flex justify-between"><span className="text-gray-600">False Alarms:</span><span className="text-gray-800">{audioFalseAlarms ?? '?'}</span></div> <div className="pt-2 border-t border-gray-100 mt-2"><div className="flex justify-between items-center"><span className="text-gray-600">d′ Score:</span><span className="font-medium text-green-600 text-base">{formatNum(audioDprime, 2)}</span></div></div> </div>
                                </div>
                            </div>
                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm mb-6">
                                <h3 className="text-sm font-medium text-gray-700 mb-3">Session Details</h3>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm"> <div> <div className="text-gray-500">Level Played</div> <div className="font-medium">{playedCombinedDisplay}</div> </div> <div> <div className="text-gray-500">Trials</div> <div className="font-medium">{trialsCompleted} / {totalTrialsPlanned}</div> </div> <div> <div className="text-gray-500">Duration</div> <div className="font-medium">{formatDuration(durationMs)}</div> </div> <div> <div className="text-gray-500">Overall Acc.</div> <div className="font-medium">{formatAcc(overallAccuracy)}%</div> </div> </div>
                            </div>
                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                <div className="flex justify-between items-center mb-3"> <h3 className="text-sm font-medium text-gray-700"> Performance Scale Reference (d′) </h3> {overallDprime !== null && (<span className="text-xs text-gray-500"> Your d′: {formatNum(overallDprime, 2)} </span>)} </div>
                                { thresholdsValid ? ( <> <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2"> <div className="space-y-2 text-sm"> <div className="flex items-center"><div className="w-3 h-3 rounded-full mr-2 shrink-0 bg-green-500"></div><span className={`min-w-[80px] text-gray-700 ${performanceCategory === 'excellent' ? 'font-semibold' : 'font-medium'}`}>Excellent:</span><span className={`ml-2 ${performanceCategory === 'excellent' ? 'text-indigo-600 font-semibold' : 'text-gray-600'}`}>≥ {formatNum(excellentThreshold, 2)}</span></div> <div className="flex items-center"><div className="w-3 h-3 rounded-full mr-2 shrink-0 bg-blue-500"></div><span className={`min-w-[80px] text-gray-700 ${performanceCategory === 'very-good' ? 'font-semibold' : 'font-medium'}`}>Very Good:</span><span className={`ml-2 ${performanceCategory === 'very-good' ? 'text-indigo-600 font-semibold' : 'text-gray-600'}`}>{formatNum(veryGoodT, 2)} - {formatNum(excellentThreshold, 2)}</span></div> <div className="flex items-center"><div className="w-3 h-3 rounded-full mr-2 shrink-0 bg-indigo-500"></div><span className={`min-w-[80px] text-gray-700 ${performanceCategory === 'good' ? 'font-semibold' : 'font-medium'}`}>Good:</span><span className={`ml-2 ${performanceCategory === 'good' ? 'text-indigo-600 font-semibold' : 'text-gray-600'}`}>{formatNum(goodThreshold, 2)} - {formatNum(veryGoodT, 2)}</span></div> </div> <div className="space-y-2 text-sm"> <div className="flex items-center"><div className="w-3 h-3 rounded-full mr-2 shrink-0 bg-amber-500"></div><span className={`min-w-[80px] text-gray-700 ${performanceCategory === 'fair' ? 'font-semibold' : 'font-medium'}`}>Fair:</span><span className={`ml-2 ${performanceCategory === 'fair' ? 'text-indigo-600 font-semibold' : 'text-gray-600'}`}>{formatNum(fairThreshold, 2)} - {formatNum(goodThreshold, 2)}</span></div> <div className="flex items-center"><div className="w-3 h-3 rounded-full mr-2 shrink-0 bg-red-500"></div><span className={`min-w-[80px] text-gray-700 ${performanceCategory === 'needs-improvement' ? 'font-semibold' : 'font-medium'}`}>Needs Imp.:</span><span className={`ml-2 ${performanceCategory === 'needs-improvement' ? 'text-indigo-600 font-semibold' : 'text-gray-600'}`}>&lt; {formatNum(fairThreshold, 2)}</span></div> <div className="flex items-center h-[20px]"> {performanceCategory === 'unknown' && (<> <div className="w-3 h-3 rounded-full mr-2 shrink-0 bg-gray-500"></div> <span className="min-w-[80px] text-gray-700 font-semibold">Unknown</span> </>)} </div> </div> </div> <p className="text-xs text-gray-500 mt-3">Note: These thresholds guided your level progression.</p> </> ) : ( <p className="text-sm text-gray-500">Performance scale reference thresholds not available.</p> )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Action Buttons Footer - REVISED */}
                <div className="px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
                    <div className="space-y-3">
                        {/* Main Action Buttons Container */}
                        <div className={`grid grid-cols-1 ${actionButtons.length === 3 ? 'sm:grid-cols-3' : (actionButtons.length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-1')} gap-3`}>
                            {actionButtons.map((btn, index) => (
                                <button
                                    key={index}
                                    onClick={btn.action}
                                    className={`w-full inline-flex items-center justify-center px-4 py-2 border text-base font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
                                        btn.style === "primary" 
                                        ? 'border-transparent text-white bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'
                                        : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-indigo-500'
                                    }`}
                                    title={btn.title}
                                >
                                    {btn.icon && <FontAwesomeIcon icon={btn.icon} className="w-4 h-4 mr-2" />}
                                    {btn.text}
                                </button>
                            ))}
                        </div>
                        
                        {/* Dashboard Button */}
                        <div className="flex justify-center pt-2"> 
                             <button
                                onClick={onDashboard}
                                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                                title="Return to the main dashboard"
                             >
                                 <FontAwesomeIcon icon={faHouse} className="w-3.5 h-3.5 mr-1.5" />
                                Dashboard
                             </button>
                        </div>
                    </div>
                </div>
            </div> {/* End Results Card */}

            {/* Comment Modal */}
            <CommentModal
                isOpen={isCommentModalOpen}
                onClose={handleCloseJournalModal}
                onSave={handleSaveJournalEntry}
                availableTags={activeProfile?.journalTags || []}
                title="Add Note & Tags to Session Summary"
                isSaving={isSavingToJournal}
                saveStatus={journalSaveStatus}
            />
            
            {/* Confirmation Modal for "Back to Previous Stored" */}
            <ConfirmationModal
                isOpen={isConfirmBackModalOpen}
                onClose={cancelGoBack}
                onConfirm={confirmGoBackAndStart}
                title="Go Back to Previous Level?"
                confirmText="Go Back"
                cancelText="Cancel"
            >
                <p>Are you sure you want to go back to level {prevNBackStoredLevelDisplay}?</p>
                {nBackPlayed && prevNBackStoredLevelDisplay && nBackPlayed > 2 && ( // Ensure nBackPlayed > 2 for this message
                    <p className="text-sm text-gray-500 mt-1">This will take you to the difficulty you last played at for {nBackPlayed -1}-Back.</p>
                )}
            </ConfirmationModal>
        </div>
    );
};

export default GameResults;